import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Cookie, Settings, BarChart3, Cog, Info } from 'lucide-react';
import { Button } from '../components/ui/button';

export const CookiePolicy = () => {
  const { language, t } = useLanguage();

  const content = {
    en: {
      title: 'Cookie Policy',
      subtitle: 'How we use cookies and similar technologies',
      lastUpdated: 'Last updated: February 2025',
      intro: {
        title: 'What Are Cookies',
        text: 'Cookies are small text files placed on your device when you visit a website. They help websites function properly, improve user experience, and provide information to website owners. TRH Steel uses cookies in accordance with EU regulations, including the ePrivacy Directive and GDPR.'
      },
      types: {
        title: 'Types of Cookies We Use',
        categories: [
          {
            icon: 'Settings',
            name: 'Strictly Necessary Cookies',
            description: 'These cookies are essential for the website to function properly. They enable basic features like page navigation, access to secure areas, and form submissions. The website cannot function properly without these cookies.',
            examples: ['Session management', 'Security tokens', 'Load balancing'],
            retention: 'Session / Up to 24 hours',
            canDisable: false
          },
          {
            icon: 'Cog',
            name: 'Functional Cookies',
            description: 'These cookies allow the website to remember choices you make (such as your language preference) and provide enhanced, personalized features.',
            examples: ['Language preference', 'Region selection', 'User interface settings'],
            retention: 'Up to 1 year',
            canDisable: true
          },
          {
            icon: 'BarChart3',
            name: 'Analytics Cookies',
            description: 'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve our website performance and user experience.',
            examples: ['Page visit counts', 'Traffic sources', 'User journey analysis'],
            retention: 'Up to 26 months',
            canDisable: true
          }
        ]
      },
      consent: {
        title: 'Cookie Consent',
        text: 'When you first visit our website, you will be presented with a cookie consent banner. You can choose to accept all cookies, accept only necessary cookies, or customize your preferences. You can change your cookie preferences at any time by clicking on the "Cookie Settings" link in the website footer.',
        note: 'Strictly necessary cookies do not require consent as they are essential for the website to function.'
      },
      thirdParty: {
        title: 'Third-Party Cookies',
        text: 'Some cookies on our website are placed by third-party services that appear on our pages. We do not control these cookies. The following third-party services may place cookies:',
        services: [
          { name: 'Google Analytics', purpose: 'Website traffic analysis', policy: 'Google Privacy Policy' }
        ]
      },
      management: {
        title: 'Managing Cookies',
        text: 'You can control and manage cookies in several ways:',
        options: [
          { method: 'Browser Settings', desc: 'Most web browsers allow you to control cookies through their settings. You can usually find these in the "Options" or "Preferences" menu.' },
          { method: 'Cookie Consent Banner', desc: 'Use our cookie consent banner when you first visit the site or access cookie settings from the footer.' },
          { method: 'Third-Party Opt-Out', desc: 'For analytics cookies, you can use tools like Google Analytics Opt-out Browser Add-on.' }
        ],
        warning: 'Please note that disabling certain cookies may affect the functionality of this website.'
      },
      contact: {
        title: 'Contact Us',
        text: 'If you have questions about our use of cookies, please contact us at:',
        email: 'privacy@trhsteel.com'
      }
    },
    es: {
      title: 'Política de Cookies',
      subtitle: 'Cómo utilizamos cookies y tecnologías similares',
      lastUpdated: 'Última actualización: Febrero 2025',
      intro: {
        title: '¿Qué Son las Cookies?',
        text: 'Las cookies son pequeños archivos de texto que se colocan en su dispositivo cuando visita un sitio web. Ayudan a que los sitios web funcionen correctamente, mejoran la experiencia del usuario y proporcionan información a los propietarios del sitio web. TRH Steel utiliza cookies de acuerdo con las regulaciones de la UE, incluyendo la Directiva de Privacidad Electrónica y el RGPD.'
      },
      types: {
        title: 'Tipos de Cookies que Utilizamos',
        categories: [
          {
            icon: 'Settings',
            name: 'Cookies Estrictamente Necesarias',
            description: 'Estas cookies son esenciales para que el sitio web funcione correctamente. Permiten funciones básicas como la navegación por páginas, el acceso a áreas seguras y el envío de formularios. El sitio web no puede funcionar correctamente sin estas cookies.',
            examples: ['Gestión de sesiones', 'Tokens de seguridad', 'Equilibrio de carga'],
            retention: 'Sesión / Hasta 24 horas',
            canDisable: false
          },
          {
            icon: 'Cog',
            name: 'Cookies Funcionales',
            description: 'Estas cookies permiten que el sitio web recuerde las opciones que usted elige (como su preferencia de idioma) y proporcionen funciones mejoradas y personalizadas.',
            examples: ['Preferencia de idioma', 'Selección de región', 'Configuración de interfaz'],
            retention: 'Hasta 1 año',
            canDisable: true
          },
          {
            icon: 'BarChart3',
            name: 'Cookies de Análisis',
            description: 'Estas cookies nos ayudan a entender cómo los visitantes interactúan con nuestro sitio web al recopilar y reportar información de forma anónima. Esto nos ayuda a mejorar el rendimiento de nuestro sitio web y la experiencia del usuario.',
            examples: ['Conteo de visitas a páginas', 'Fuentes de tráfico', 'Análisis del recorrido del usuario'],
            retention: 'Hasta 26 meses',
            canDisable: true
          }
        ]
      },
      consent: {
        title: 'Consentimiento de Cookies',
        text: 'Cuando visite nuestro sitio web por primera vez, se le presentará un banner de consentimiento de cookies. Puede elegir aceptar todas las cookies, aceptar solo las cookies necesarias o personalizar sus preferencias. Puede cambiar sus preferencias de cookies en cualquier momento haciendo clic en el enlace "Configuración de Cookies" en el pie de página del sitio web.',
        note: 'Las cookies estrictamente necesarias no requieren consentimiento ya que son esenciales para que el sitio web funcione.'
      },
      thirdParty: {
        title: 'Cookies de Terceros',
        text: 'Algunas cookies en nuestro sitio web son colocadas por servicios de terceros que aparecen en nuestras páginas. No controlamos estas cookies. Los siguientes servicios de terceros pueden colocar cookies:',
        services: [
          { name: 'Google Analytics', purpose: 'Análisis de tráfico web', policy: 'Política de Privacidad de Google' }
        ]
      },
      management: {
        title: 'Gestión de Cookies',
        text: 'Puede controlar y gestionar las cookies de varias maneras:',
        options: [
          { method: 'Configuración del Navegador', desc: 'La mayoría de los navegadores web le permiten controlar las cookies a través de su configuración. Generalmente puede encontrarlas en el menú "Opciones" o "Preferencias".' },
          { method: 'Banner de Consentimiento', desc: 'Use nuestro banner de consentimiento de cookies cuando visite el sitio por primera vez o acceda a la configuración de cookies desde el pie de página.' },
          { method: 'Exclusión de Terceros', desc: 'Para cookies de análisis, puede usar herramientas como el Complemento de exclusión de Google Analytics.' }
        ],
        warning: 'Tenga en cuenta que deshabilitar ciertas cookies puede afectar la funcionalidad de este sitio web.'
      },
      contact: {
        title: 'Contáctenos',
        text: 'Si tiene preguntas sobre nuestro uso de cookies, contáctenos en:',
        email: 'privacy@trhsteel.com'
      }
    },
    fr: {
      title: 'Politique de Cookies',
      subtitle: 'Comment nous utilisons les cookies et technologies similaires',
      lastUpdated: 'Dernière mise à jour: Février 2025',
      intro: {
        title: 'Qu\'est-ce que les Cookies',
        text: 'Les cookies sont de petits fichiers texte placés sur votre appareil lorsque vous visitez un site web. Ils aident les sites web à fonctionner correctement, améliorent l\'expérience utilisateur et fournissent des informations aux propriétaires de sites web. TRH Steel utilise des cookies conformément aux réglementations de l\'UE, y compris la directive ePrivacy et le RGPD.'
      },
      types: {
        title: 'Types de Cookies Utilisés',
        categories: [
          {
            icon: 'Settings',
            name: 'Cookies Strictement Nécessaires',
            description: 'Ces cookies sont essentiels au bon fonctionnement du site web. Ils permettent des fonctionnalités de base comme la navigation entre les pages, l\'accès aux zones sécurisées et l\'envoi de formulaires. Le site web ne peut pas fonctionner correctement sans ces cookies.',
            examples: ['Gestion des sessions', 'Jetons de sécurité', 'Équilibrage de charge'],
            retention: 'Session / Jusqu\'à 24 heures',
            canDisable: false
          },
          {
            icon: 'Cog',
            name: 'Cookies Fonctionnels',
            description: 'Ces cookies permettent au site web de mémoriser vos choix (comme votre préférence linguistique) et de fournir des fonctionnalités améliorées et personnalisées.',
            examples: ['Préférence linguistique', 'Sélection de région', 'Paramètres d\'interface'],
            retention: 'Jusqu\'à 1 an',
            canDisable: true
          },
          {
            icon: 'BarChart3',
            name: 'Cookies Analytiques',
            description: 'Ces cookies nous aident à comprendre comment les visiteurs interagissent avec notre site web en collectant et rapportant des informations de manière anonyme. Cela nous aide à améliorer les performances de notre site web et l\'expérience utilisateur.',
            examples: ['Comptage des visites', 'Sources de trafic', 'Analyse du parcours utilisateur'],
            retention: 'Jusqu\'à 26 mois',
            canDisable: true
          }
        ]
      },
      consent: {
        title: 'Consentement aux Cookies',
        text: 'Lors de votre première visite sur notre site web, une bannière de consentement aux cookies vous sera présentée. Vous pouvez choisir d\'accepter tous les cookies, d\'accepter uniquement les cookies nécessaires ou de personnaliser vos préférences. Vous pouvez modifier vos préférences de cookies à tout moment en cliquant sur le lien "Paramètres des Cookies" dans le pied de page du site.',
        note: 'Les cookies strictement nécessaires ne requièrent pas de consentement car ils sont essentiels au fonctionnement du site.'
      },
      thirdParty: {
        title: 'Cookies Tiers',
        text: 'Certains cookies sur notre site web sont placés par des services tiers qui apparaissent sur nos pages. Nous ne contrôlons pas ces cookies. Les services tiers suivants peuvent placer des cookies:',
        services: [
          { name: 'Google Analytics', purpose: 'Analyse du trafic web', policy: 'Politique de confidentialité Google' }
        ]
      },
      management: {
        title: 'Gestion des Cookies',
        text: 'Vous pouvez contrôler et gérer les cookies de plusieurs façons:',
        options: [
          { method: 'Paramètres du Navigateur', desc: 'La plupart des navigateurs web vous permettent de contrôler les cookies via leurs paramètres. Vous pouvez généralement les trouver dans le menu "Options" ou "Préférences".' },
          { method: 'Bannière de Consentement', desc: 'Utilisez notre bannière de consentement aux cookies lors de votre première visite ou accédez aux paramètres des cookies depuis le pied de page.' },
          { method: 'Exclusion Tiers', desc: 'Pour les cookies analytiques, vous pouvez utiliser des outils comme le module de désactivation de Google Analytics.' }
        ],
        warning: 'Veuillez noter que la désactivation de certains cookies peut affecter la fonctionnalité de ce site web.'
      },
      contact: {
        title: 'Nous Contacter',
        text: 'Si vous avez des questions sur notre utilisation des cookies, veuillez nous contacter à:',
        email: 'privacy@trhsteel.com'
      }
    },
    de: {
      title: 'Cookie-Richtlinie',
      subtitle: 'Wie wir Cookies und ähnliche Technologien verwenden',
      lastUpdated: 'Letzte Aktualisierung: Februar 2025',
      intro: {
        title: 'Was Sind Cookies',
        text: 'Cookies sind kleine Textdateien, die auf Ihrem Gerät platziert werden, wenn Sie eine Website besuchen. Sie helfen Websites, ordnungsgemäß zu funktionieren, verbessern die Benutzererfahrung und liefern Informationen an Website-Betreiber. TRH Steel verwendet Cookies in Übereinstimmung mit EU-Vorschriften, einschließlich der ePrivacy-Richtlinie und der DSGVO.'
      },
      types: {
        title: 'Arten von Cookies',
        categories: [
          {
            icon: 'Settings',
            name: 'Unbedingt Erforderliche Cookies',
            description: 'Diese Cookies sind für das ordnungsgemäße Funktionieren der Website unerlässlich. Sie ermöglichen grundlegende Funktionen wie Seitennavigation, Zugang zu sicheren Bereichen und Formularübermittlungen. Die Website kann ohne diese Cookies nicht richtig funktionieren.',
            examples: ['Sitzungsverwaltung', 'Sicherheitstoken', 'Lastverteilung'],
            retention: 'Sitzung / Bis zu 24 Stunden',
            canDisable: false
          },
          {
            icon: 'Cog',
            name: 'Funktionale Cookies',
            description: 'Diese Cookies ermöglichen es der Website, sich an Ihre Entscheidungen zu erinnern (wie Ihre Sprachpräferenz) und erweiterte, personalisierte Funktionen bereitzustellen.',
            examples: ['Sprachpräferenz', 'Regionsauswahl', 'Oberflächeneinstellungen'],
            retention: 'Bis zu 1 Jahr',
            canDisable: true
          },
          {
            icon: 'BarChart3',
            name: 'Analyse-Cookies',
            description: 'Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren, indem sie Informationen anonym sammeln und melden. Dies hilft uns, die Leistung unserer Website und die Benutzererfahrung zu verbessern.',
            examples: ['Seitenbesuchszählung', 'Traffic-Quellen', 'Benutzerreise-Analyse'],
            retention: 'Bis zu 26 Monate',
            canDisable: true
          }
        ]
      },
      consent: {
        title: 'Cookie-Einwilligung',
        text: 'Bei Ihrem ersten Besuch auf unserer Website wird Ihnen ein Cookie-Einwilligungsbanner angezeigt. Sie können alle Cookies akzeptieren, nur notwendige Cookies akzeptieren oder Ihre Präferenzen anpassen. Sie können Ihre Cookie-Präferenzen jederzeit ändern, indem Sie auf den Link "Cookie-Einstellungen" in der Fußzeile der Website klicken.',
        note: 'Unbedingt erforderliche Cookies benötigen keine Einwilligung, da sie für das Funktionieren der Website wesentlich sind.'
      },
      thirdParty: {
        title: 'Drittanbieter-Cookies',
        text: 'Einige Cookies auf unserer Website werden von Drittanbieterdiensten platziert, die auf unseren Seiten erscheinen. Wir kontrollieren diese Cookies nicht. Die folgenden Drittanbieterdienste können Cookies platzieren:',
        services: [
          { name: 'Google Analytics', purpose: 'Website-Traffic-Analyse', policy: 'Google Datenschutzerklärung' }
        ]
      },
      management: {
        title: 'Cookies Verwalten',
        text: 'Sie können Cookies auf verschiedene Weise kontrollieren und verwalten:',
        options: [
          { method: 'Browser-Einstellungen', desc: 'Die meisten Webbrowser ermöglichen es Ihnen, Cookies über ihre Einstellungen zu steuern. Diese finden Sie normalerweise im Menü "Optionen" oder "Einstellungen".' },
          { method: 'Einwilligungsbanner', desc: 'Verwenden Sie unser Cookie-Einwilligungsbanner bei Ihrem ersten Besuch oder greifen Sie über die Fußzeile auf die Cookie-Einstellungen zu.' },
          { method: 'Drittanbieter-Opt-Out', desc: 'Für Analyse-Cookies können Sie Tools wie das Google Analytics Opt-out Browser-Add-on verwenden.' }
        ],
        warning: 'Bitte beachten Sie, dass das Deaktivieren bestimmter Cookies die Funktionalität dieser Website beeinträchtigen kann.'
      },
      contact: {
        title: 'Kontakt',
        text: 'Bei Fragen zu unserer Verwendung von Cookies kontaktieren Sie uns bitte unter:',
        email: 'privacy@trhsteel.com'
      }
    },
    nl: {
      title: 'Cookiebeleid',
      subtitle: 'Hoe wij cookies en soortgelijke technologieën gebruiken',
      lastUpdated: 'Laatst bijgewerkt: Februari 2025',
      intro: {
        title: 'Wat Zijn Cookies',
        text: 'Cookies zijn kleine tekstbestanden die op uw apparaat worden geplaatst wanneer u een website bezoekt. Ze helpen websites correct te functioneren, verbeteren de gebruikerservaring en verstrekken informatie aan website-eigenaren. TRH Steel gebruikt cookies in overeenstemming met EU-regelgeving, waaronder de ePrivacy-richtlijn en AVG.'
      },
      types: {
        title: 'Soorten Cookies',
        categories: [
          {
            icon: 'Settings',
            name: 'Strikt Noodzakelijke Cookies',
            description: 'Deze cookies zijn essentieel voor het correct functioneren van de website. Ze maken basisfuncties mogelijk zoals paginanavigatie, toegang tot beveiligde gebieden en het verzenden van formulieren. De website kan zonder deze cookies niet correct functioneren.',
            examples: ['Sessiebeheer', 'Beveiligingstokens', 'Loadbalancing'],
            retention: 'Sessie / Tot 24 uur',
            canDisable: false
          },
          {
            icon: 'Cog',
            name: 'Functionele Cookies',
            description: 'Deze cookies stellen de website in staat om keuzes die u maakt (zoals uw taalvoorkeur) te onthouden en verbeterde, gepersonaliseerde functies te bieden.',
            examples: ['Taalvoorkeur', 'Regioselectie', 'Interface-instellingen'],
            retention: 'Tot 1 jaar',
            canDisable: true
          },
          {
            icon: 'BarChart3',
            name: 'Analytische Cookies',
            description: 'Deze cookies helpen ons te begrijpen hoe bezoekers met onze website omgaan door anoniem informatie te verzamelen en te rapporteren. Dit helpt ons de prestaties van onze website en de gebruikerservaring te verbeteren.',
            examples: ['Pagina bezoek telling', 'Verkeersbronnen', 'Gebruikersreis analyse'],
            retention: 'Tot 26 maanden',
            canDisable: true
          }
        ]
      },
      consent: {
        title: 'Cookie Toestemming',
        text: 'Bij uw eerste bezoek aan onze website krijgt u een cookie-toestemmingsbanner te zien. U kunt ervoor kiezen alle cookies te accepteren, alleen noodzakelijke cookies te accepteren of uw voorkeuren aan te passen. U kunt uw cookievoorkeuren op elk moment wijzigen door te klikken op de link "Cookie-instellingen" in de voettekst van de website.',
        note: 'Strikt noodzakelijke cookies vereisen geen toestemming omdat ze essentieel zijn voor het functioneren van de website.'
      },
      thirdParty: {
        title: 'Cookies van Derden',
        text: 'Sommige cookies op onze website worden geplaatst door diensten van derden die op onze pagina\'s verschijnen. Wij hebben geen controle over deze cookies. De volgende diensten van derden kunnen cookies plaatsen:',
        services: [
          { name: 'Google Analytics', purpose: 'Website verkeer analyse', policy: 'Google Privacybeleid' }
        ]
      },
      management: {
        title: 'Cookies Beheren',
        text: 'U kunt cookies op verschillende manieren beheren:',
        options: [
          { method: 'Browserinstellingen', desc: 'De meeste webbrowsers stellen u in staat cookies te beheren via hun instellingen. Deze vindt u meestal in het menu "Opties" of "Voorkeuren".' },
          { method: 'Toestemmingsbanner', desc: 'Gebruik onze cookie-toestemmingsbanner bij uw eerste bezoek of open de cookie-instellingen vanuit de voettekst.' },
          { method: 'Opt-out Derden', desc: 'Voor analytische cookies kunt u tools gebruiken zoals de Google Analytics Opt-out Browser Add-on.' }
        ],
        warning: 'Houd er rekening mee dat het uitschakelen van bepaalde cookies de functionaliteit van deze website kan beïnvloeden.'
      },
      contact: {
        title: 'Contact',
        text: 'Als u vragen heeft over ons gebruik van cookies, neem dan contact met ons op via:',
        email: 'privacy@trhsteel.com'
      }
    }
  };

  const c = content[language] || content.en;

  const iconMap = {
    'Settings': Settings,
    'Cog': Cog,
    'BarChart3': BarChart3
  };

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="cookie-policy-page">
      {/* Hero */}
      <section className="py-16 md:py-20 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-3 mb-4">
            <Cookie className="h-8 w-8 text-green-500" />
            <span className="text-green-500 text-sm uppercase tracking-wider font-bold">EU Compliant</span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight mb-4">
            {c.title}
          </h1>
          <p className="text-slate-400">{c.subtitle}</p>
          <p className="text-slate-500 text-sm mt-4">{c.lastUpdated}</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-slate-200 p-8 md:p-12 space-y-10">
            
            {/* What Are Cookies */}
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-4 flex items-center">
                <Info className="h-5 w-5 mr-2 text-green-600" />
                {c.intro.title}
              </h2>
              <p className="text-slate-600 leading-relaxed">{c.intro.text}</p>
            </div>

            {/* Types of Cookies */}
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-6">
                {c.types.title}
              </h2>
              <div className="space-y-6">
                {c.types.categories.map((cat, i) => {
                  const IconComponent = iconMap[cat.icon] || Settings;
                  return (
                    <div key={i} className="border border-slate-200 p-6 bg-slate-50">
                      <div className="flex items-start space-x-4">
                        <div className={`w-12 h-12 flex items-center justify-center flex-shrink-0 ${cat.canDisable ? 'bg-slate-700' : 'bg-green-700'}`}>
                          <IconComponent className="h-6 w-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="font-heading text-lg font-bold text-slate-900 uppercase">{cat.name}</h3>
                            <span className={`text-xs font-bold px-2 py-1 ${cat.canDisable ? 'bg-slate-200 text-slate-600' : 'bg-green-100 text-green-700'}`}>
                              {cat.canDisable ? t('optional') : t('required')}
                            </span>
                          </div>
                          <p className="text-slate-600 text-sm mb-3">{cat.description}</p>
                          <div className="flex flex-wrap gap-2 mb-2">
                            {cat.examples.map((ex, j) => (
                              <span key={j} className="text-xs bg-slate-200 text-slate-600 px-2 py-1">{ex}</span>
                            ))}
                          </div>
                          <p className="text-xs text-slate-500">
                            <strong>{t('retention')}:</strong> {cat.retention}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Consent */}
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-4">
                {c.consent.title}
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">{c.consent.text}</p>
              <div className="bg-green-50 border border-green-200 p-4 mb-4">
                <p className="text-green-800 text-sm">{c.consent.note}</p>
              </div>
              <Button
                onClick={() => window.openCookieSettings && window.openCookieSettings()}
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-none uppercase tracking-wider font-bold"
                data-testid="cookie-manage-preferences-btn"
              >
                <Settings className="h-4 w-4 mr-2" />
                {language === 'es' ? 'Gestionar Preferencias' : language === 'fr' ? 'Gérer les Préférences' : language === 'de' ? 'Einstellungen Verwalten' : language === 'nl' ? 'Voorkeuren Beheren' : 'Manage Preferences'}
              </Button>
            </div>

            {/* Third Party */}
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-4">
                {c.thirdParty.title}
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">{c.thirdParty.text}</p>
              <div className="bg-slate-50 border border-slate-200">
                {c.thirdParty.services.map((service, i) => (
                  <div key={i} className="flex justify-between p-4 border-b border-slate-200 last:border-0">
                    <span className="font-medium text-slate-900">{service.name}</span>
                    <span className="text-slate-600 text-sm">{service.purpose}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Management */}
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-4">
                {c.management.title}
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">{c.management.text}</p>
              <div className="space-y-3">
                {c.management.options.map((opt, i) => (
                  <div key={i} className="bg-slate-50 p-4 border-l-4 border-slate-400">
                    <p className="font-bold text-slate-900">{opt.method}</p>
                    <p className="text-slate-600 text-sm">{opt.desc}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 bg-amber-50 border border-amber-200 p-4">
                <p className="text-amber-800 text-sm">{c.management.warning}</p>
              </div>
            </div>

            {/* Contact */}
            <div className="bg-slate-900 text-white p-6">
              <h2 className="font-heading text-lg font-bold uppercase tracking-tight mb-2">
                {c.contact.title}
              </h2>
              <p className="text-slate-400 text-sm mb-4">{c.contact.text}</p>
              <a href={`mailto:${c.contact.email}`} className="text-green-400 hover:text-green-300">{c.contact.email}</a>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};
